import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { ThemeProvider } from '@mui/material'
import { pantryColors } from '@pantry/ui'
import { useGalleryState, MAX_SPATULA_COPIES } from '../../hooks/useGalleryState'
import { useGalleryContext } from '../../context/GalleryContext'
import AssetLibrary from './AssetLibrary'
import UploadZone from './UploadZone'
import type { PantryMediaFile, GalleryPickerTab } from './types'
import { ASSET_TYPE_OPTIONS } from './types'

const p = pantryColors

const tokens = {
  green: '#2563EB',
  greenH: '#1D4ED8',
  greenLight: '#EFF6FF',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  rSm: 6,
  rMd: 10,
  rLg: 16,
}

// MUI theme mock for pantry - import from app if available
const theme = { palette: {} }

export interface GalleryPickerProps {
  open: boolean
  onClose: () => void
  title?: string
  images?: PantryMediaFile[]
  handleSave: (files: PantryMediaFile[]) => void
  multiple?: boolean
  noUploadVideo?: boolean
  showTypes?: string[]
  selectTypes?: string[]
  acceptTypes?: string
  maximumMbSize?: number
  currentImageLength?: number
  // Product editor only: also offer Breadstack catalog images not copied yet.
  includeSpatulaCatalog?: boolean
  // Product editor only: the value the storefront uses when Alt Text is left
  // blank (the product name). Shown as the placeholder and helper text.
  altTextFallback?: string
}

export const GalleryPicker: React.FC<GalleryPickerProps> = ({
  open,
  onClose,
  title = 'Select Image',
  images = [],
  handleSave,
  multiple = false,
  noUploadVideo = false,
  showTypes,
  selectTypes,
  acceptTypes = '.jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.wmv,.mpg,.ogv,.3gp,.3g2,.webm,.svg',
  maximumMbSize = 10,
  currentImageLength = 0,
  includeSpatulaCatalog = false,
  altTextFallback,
}) => {
  const { orgId, getFolderId, isFileImage } = useGalleryContext()
  const galleryState = useGalleryState({
    orgId,
    isFileImage,
    getFolderId,
    enabled: open,
    includeSpatulaCatalog,
  })

  const [tab, setTab] = useState<GalleryPickerTab>('library')

  useEffect(() => {
    if (open) {
      // Normalize initial images to match API response format
      const normalizedImages = images.map(img => ({
        ...img,
        src: img.src || img.public_url,
        thumbnail: img.thumbnail || img.public_url || img.src,
      }))
      galleryState.setFileSelected(normalizedImages)
      galleryState.setActiveFile(null)
      setTab('library')
      galleryState.setSelectedFolderId(galleryState.MEDIA_FOLDER_STATIC.ALL_FILE)
    }
  }, [open])

  const validFiles = galleryState.fileSelected.filter(
    (f) => f && Object.keys(f).length > 0,
  )
  // Save waits until every picked catalog image has been copied into Breadstack.
  const saveDisabled = validFiles.length === 0 || galleryState.isCopyingSpatula
  const selectedFile: PantryMediaFile | undefined = galleryState.activeFile
  const previewSrc =
    selectedFile?.src || selectedFile?.public_url || selectedFile?.thumbnail || ''

  // Each picked image's details: what the owner typed, else the image's own values.
  const detailsOf = (file?: PantryMediaFile) => ({
    altText: file?._edits?.alternative_text ?? (file?.alternative_text || file?.alt || ''),
    titleAttr: file?._edits?.title ?? (file?.title || ''),
    caption: file?._edits?.caption ?? (file?.caption || ''),
    fileName: file?._edits?.name ?? (file?.name || ''),
    assetTypes:
      file?._edits?.asset_types ?? (Array.isArray(file?.asset_types) ? file.asset_types : []),
  })
  const { altText, titleAttr, caption, fileName: fileNameInput, assetTypes } =
    detailsOf(selectedFile)

  const toggleAssetType = (value: string) => {
    galleryState.editActiveFile({
      asset_types: assetTypes.includes(value)
        ? assetTypes.filter((v) => v !== value)
        : [...assetTypes, value],
    })
  }

  const didFinishUpload = () => {
    galleryState.setSelectedFolderId(galleryState.MEDIA_FOLDER_STATIC.ALL_FILE)
    setTab('library')
  }

  const handleSaveClick = () => {
    const isNumeric = (value: unknown) => /^-?\d+$/.test(String(value))
    const result: PantryMediaFile[] = validFiles.map((file) => {
      const details = detailsOf(file)
      return {
        id: isNumeric(file.id) ? file.id : undefined,
        _id: file._id,
        name: details.fileName || file.name || '',
        src: file.src,
        thumbnail: file.public_url || file.src,
        alt: details.altText,
        alternative_text: details.altText,
        title: details.titleAttr,
        caption: details.caption,
        asset_types: details.assetTypes,
        content_type: file.content_type,
        id_select: file.id,
      }
    })
    handleSave(result)
  }

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '—'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString()
    } catch {
      return '—'
    }
  }

  if (!open) return null

  const modal = (
    <ThemeProvider theme={theme}>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#fff',
            borderRadius: tokens.rLg,
            width: 960,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: 'calc(100vh - 48px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px 20px',
              borderBottom: `1px solid ${tokens.gray200}`,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                background: tokens.greenLight,
                color: tokens.green,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ width: 14, height: 14 }}
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </div>
            <h3
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 600,
                color: tokens.gray800,
                margin: 0,
              }}
            >
              {title}
            </h3>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                color: tokens.gray400,
                fontSize: 20,
                lineHeight: 1,
                padding: '2px 6px',
                borderRadius: 4,
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.color =
                  tokens.gray800)
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.color =
                  tokens.gray400)
              }
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div
            style={{
              overflowY: 'auto',
              overflowX: 'hidden',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Top: Preview + SEO details */}
            <div
              style={{
                display: 'flex',
                borderBottom: `1px solid ${tokens.gray200}`,
                flexShrink: 0,
              }}
            >
              {/* Left: Image Preview */}
              <div
                style={{
                  width: 280,
                  flexShrink: 0,
                  background: tokens.gray50,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 24,
                  borderRight: `1px solid ${tokens.gray200}`,
                }}
              >
                <div
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    borderRadius: tokens.rMd,
                    border: `1px solid ${tokens.gray200}`,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                  }}
                >
                  {previewSrc && selectedFile?.content_type?.startsWith('video') ? (
                    <video
                      src={previewSrc}
                      controls
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : previewSrc ? (
                    <img
                      src={previewSrc}
                      alt={altText || selectedFile?.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                        color: tokens.gray400,
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        style={{ width: 40, height: 40 }}
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span style={{ fontSize: 12 }}>No file selected</span>
                    </div>
                  )}
                </div>
                {selectedFile && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px 16px',
                      marginTop: 14,
                      width: '100%',
                    }}
                  >
                    {selectedFile.width && selectedFile.height && (
                      <div style={{ fontSize: 11, color: tokens.gray400 }}>
                        Dimensions:{' '}
                        <span style={{ fontWeight: 600, color: tokens.gray600 }}>
                          {selectedFile.width}×{selectedFile.height}
                        </span>
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: tokens.gray400 }}>
                      Size:{' '}
                      <span style={{ fontWeight: 600, color: tokens.gray600 }}>
                        {formatFileSize(selectedFile.size)}
                      </span>
                    </div>
                    {selectedFile.content_type && (
                      <div style={{ fontSize: 11, color: tokens.gray400 }}>
                        Format:{' '}
                        <span style={{ fontWeight: 600, color: tokens.gray600 }}>
                          {selectedFile.content_type.split('/').pop()?.toUpperCase() || '—'}
                        </span>
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: tokens.gray400 }}>
                      Added:{' '}
                      <span style={{ fontWeight: 600, color: tokens.gray600 }}>
                        {formatDate(selectedFile.created_at)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Right: SEO & Metadata fields */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: 20,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: tokens.gray600,
                    marginBottom: 2,
                  }}
                >
                  Image SEO &amp; Metadata
                </div>

                {/* Alt Text */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label
                    htmlFor="gallery-picker-alt-text"
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: tokens.gray600,
                    }}
                  >
                    Alt Text{' '}
                    <span style={{ color: tokens.gray400, fontWeight: 400 }}>
                      {altTextFallback
                        ? '— optional, defaults to the product name'
                        : '— required for accessibility & SEO'}
                    </span>
                  </label>
                  <input
                    id="gallery-picker-alt-text"
                    disabled={!selectedFile}
                    type="text"
                    value={altText}
                    onChange={(e) =>
                      galleryState.editActiveFile({ alternative_text: e.target.value })
                    }
                    placeholder={
                      altTextFallback
                        ? `Leave blank to use "${altTextFallback}"`
                        : 'Describe the image for accessibility'
                    }
                    style={{
                      border: `1px solid ${tokens.gray200}`,
                      borderRadius: tokens.rSm,
                      padding: '8px 12px',
                      fontSize: 13,
                      outline: 'none',
                      color: tokens.gray800,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = tokens.green
                      e.currentTarget.style.boxShadow = `0 0 0 3px rgba(37,99,235,0.1)`
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = tokens.gray200
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                  <span style={{ fontSize: 11, color: tokens.gray400, marginTop: 2 }}>
                    Screen readers and search engines use this to understand the image content.
                    {altTextFallback &&
                      ' If left blank, the storefront uses the product name as the alt text.'}
                  </span>
                </div>

                {/* Title Attribute */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label
                    htmlFor="gallery-picker-title-attr"
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: tokens.gray600,
                    }}
                  >
                    Title Attribute{' '}
                    <span style={{ color: tokens.gray400, fontWeight: 400 }}>
                      optional
                    </span>
                  </label>
                  <input
                    id="gallery-picker-title-attr"
                    disabled={!selectedFile}
                    type="text"
                    value={titleAttr}
                    onChange={(e) => galleryState.editActiveFile({ title: e.target.value })}
                    placeholder="Tooltip text shown on hover"
                    style={{
                      border: `1px solid ${tokens.gray200}`,
                      borderRadius: tokens.rSm,
                      padding: '8px 12px',
                      fontSize: 13,
                      outline: 'none',
                      color: tokens.gray800,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = tokens.green
                      e.currentTarget.style.boxShadow = `0 0 0 3px rgba(37,99,235,0.1)`
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = tokens.gray200
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* Caption */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label
                    htmlFor="gallery-picker-caption"
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: tokens.gray600,
                    }}
                  >
                    Caption{' '}
                    <span style={{ color: tokens.gray400, fontWeight: 400 }}>
                      optional
                    </span>
                  </label>
                  <textarea
                    id="gallery-picker-caption"
                    disabled={!selectedFile}
                    value={caption}
                    onChange={(e) => galleryState.editActiveFile({ caption: e.target.value })}
                    placeholder="Short caption displayed below the image"
                    rows={2}
                    style={{
                      border: `1px solid ${tokens.gray200}`,
                      borderRadius: tokens.rSm,
                      padding: '8px 12px',
                      fontSize: 13,
                      outline: 'none',
                      color: tokens.gray800,
                      width: '100%',
                      boxSizing: 'border-box',
                      resize: 'vertical',
                      minHeight: 48,
                      fontFamily: 'inherit',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = tokens.green
                      e.currentTarget.style.boxShadow = `0 0 0 3px rgba(37,99,235,0.1)`
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = tokens.gray200
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                </div>

                {/* File Name (editable) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label
                    htmlFor="gallery-picker-file-name"
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: tokens.gray600,
                    }}
                  >
                    File Name
                  </label>
                  <input
                    id="gallery-picker-file-name"
                    disabled={!selectedFile}
                    type="text"
                    value={fileNameInput}
                    onChange={(e) => galleryState.editActiveFile({ name: e.target.value })}
                    placeholder="og-kush-front.jpg"
                    style={{
                      border: `1px solid ${tokens.gray200}`,
                      borderRadius: tokens.rSm,
                      padding: '8px 12px',
                      fontSize: 13,
                      outline: 'none',
                      color: tokens.gray800,
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = tokens.green
                      e.currentTarget.style.boxShadow = `0 0 0 3px rgba(37,99,235,0.1)`
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = tokens.gray200
                      e.currentTarget.style.boxShadow = 'none'
                    }}
                  />
                  <span style={{ fontSize: 11, color: tokens.gray400, marginTop: 2 }}>
                    SEO-friendly: lowercase, hyphens between words.
                  </span>
                </div>

                {/* URL (read-only) */}
                {(selectedFile?.src || selectedFile?.public_url) && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: tokens.gray600 }}>
                      URL
                    </div>
                    <span
                      style={{
                        fontSize: 12,
                        color: tokens.green,
                        wordBreak: 'break-all',
                      }}
                    >
                      {selectedFile.src || selectedFile.public_url}
                    </span>
                  </div>
                )}

                {/* Asset Type */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: tokens.gray600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <span>
                      Asset Type{' '}
                      <span style={{ color: tokens.gray400, fontWeight: 400 }}>
                        optional
                      </span>
                    </span>
                    <span
                      title="Categorize your assets for easier filtering and navigation in the Asset Library."
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        background: tokens.gray200,
                        color: tokens.gray400,
                        fontSize: 9,
                        fontWeight: 700,
                        cursor: 'help',
                        marginLeft: 2,
                      }}
                    >
                      ?
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px 14px',
                      marginTop: 4,
                    }}
                  >
                    {ASSET_TYPE_OPTIONS.map((opt) => {
                      const checked = assetTypes.includes(opt.value)
                      return (
                        <label
                          key={opt.value}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            fontSize: 12.5,
                            fontWeight: 400,
                            color: tokens.gray600,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!selectedFile}
                            onChange={() => toggleAssetType(opt.value)}
                            style={{
                              width: 14,
                              height: 14,
                              accentColor: tokens.green,
                              cursor: 'pointer',
                            }}
                          />
                          {opt.label}
                        </label>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom: Asset Library / Upload tabs */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              {/* Tab bar */}
              <div
                style={{
                  display: 'flex',
                  borderBottom: `1px solid ${tokens.gray200}`,
                  padding: '0 20px',
                  background: tokens.gray50,
                  flexShrink: 0,
                }}
              >
                {(['library', 'upload'] as GalleryPickerTab[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    style={{
                      padding: '10px 16px',
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: tab === t ? tokens.green : tokens.gray400,
                      cursor: 'pointer',
                      borderBottom: `2px solid ${tab === t ? tokens.green : 'transparent'}`,
                      borderTop: 'none',
                      borderLeft: 'none',
                      borderRight: 'none',
                      background: 'none',
                      transition: 'all 0.12s',
                    }}
                  >
                    {t === 'library' ? 'Asset Library' : 'Upload New'}
                  </button>
                ))}
              </div>

              {/* Tab content — no inner scroll, the dialog body (line ~263) is the single scroller */}
              <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
                {tab === 'library' && (
                  <AssetLibrary
                    currentImageLength={currentImageLength}
                    fileSelected={galleryState.fileSelected}
                    setFileSelected={galleryState.setFileSelected}
                    multiFileSelect={multiple}
                    folderSelectedId={galleryState.selectedFolderId}
                    setFolderSelectedId={galleryState.setSelectedFolderId}
                    galleryState={galleryState}
                  />
                )}
                {tab === 'upload' && (
                  <UploadZone
                    noUploadVideo={noUploadVideo}
                    didFinishUploadFiles={didFinishUpload}
                    acceptTypes={acceptTypes}
                    uploadMultiFile={multiple}
                    folderSelectedId={galleryState.selectedFolderId}
                    maximumMbSize={maximumMbSize}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '12px 20px',
              borderTop: `1px solid ${tokens.gray200}`,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <div style={{ flex: 1, fontSize: 11, color: tokens.gray400 }}>
              {galleryState.spatulaLimitReached
                ? `You can add up to ${MAX_SPATULA_COPIES} new Breadstack images at a time.`
                : galleryState.isCopyingSpatula
                  ? 'Adding images to your library…'
                  : validFiles.length > 0
                    ? `${validFiles.length} file${validFiles.length > 1 ? 's' : ''} selected`
                    : 'Select a file from the library or upload a new one.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '7px 14px',
                  borderRadius: tokens.rSm,
                  fontSize: 13,
                  fontWeight: 500,
                  color: tokens.gray600,
                  border: `1px solid ${tokens.gray300}`,
                  background: 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = tokens.gray100
                  e.currentTarget.style.color = tokens.gray800
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = tokens.gray600
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveClick}
                disabled={saveDisabled}
                style={{
                  padding: '7px 14px',
                  borderRadius: tokens.rSm,
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#fff',
                  border: 'none',
                  background: saveDisabled ? tokens.gray300 : tokens.green,
                  cursor: saveDisabled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => {
                  if (!saveDisabled)
                    e.currentTarget.style.background = tokens.greenH
                }}
                onMouseLeave={(e) => {
                  if (!saveDisabled)
                    e.currentTarget.style.background = tokens.green
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  style={{ width: 12, height: 12 }}
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </ThemeProvider>
  )

  return ReactDOM.createPortal(modal, document.body)
}

export default GalleryPicker
